import { GetTariffsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/tariffs/get-tariffs-paginated.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { PullTariffsModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/tariffs/pull-tariffs.model';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  EMSP_BO_TARIFF_FILTERS_LSK,
  EMSP_BO_TARIFF_LIST_OPTIONS_LSK,
} from '@app/config/localstorage-keys.const';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  EMSP_BO_TARIFF_LIST_DEFAULT_OPTIONS,
  EMSP_BO_TARIFF_LIST_FILTER_NAME_LIST,
  EMSP_BO_TARIFF_LIST_SORT_FIELD,
  EMSP_BO_TARIFF_LIST_SORT_ORDER,
  EmspBoTariffListOptionsModel,
} from './emsp-bo-tariff-list/emsp-bo-tariff-list.config';

// /////////////////////////////////////////////////////////////////////////////

export interface EmspBoTariffListContextProps {
  getPaginatedQueryParameters: GetTariffsPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetTariffsPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  columnOptions: EmspBoTariffListOptionsModel;
  setColumnOptions: Dispatch<SetStateAction<EmspBoTariffListOptionsModel>>;
}

export const EmspBoTariffListContext =
  createContext<EmspBoTariffListContextProps | null>(null);

export const useEmspBoTariffListContext = (): EmspBoTariffListContextProps => {
  const context = useContext(EmspBoTariffListContext);

  if (!context) {
    throw new Error(
      'useEmspBoTariffListContext must be used within EmspBoTariffListContext.Provider',
    );
  }

  return context;
};

interface EmspBoTariffListContextProviderProps {
  children: React.ReactNode;
}

export const EmspBoTariffListContextProvider = ({
  children,
}: EmspBoTariffListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetTariffsPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        id: undefined,
        cpoId: '',
        textToSearch: '',
        createdAfter: undefined,
        createdBefore: undefined,
        updatedAfter: undefined,
        updatedBefore: undefined,
        // Sortable fields
        sortField: EMSP_BO_TARIFF_LIST_SORT_FIELD,
        sortOrder: EMSP_BO_TARIFF_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...EMSP_BO_TARIFF_LIST_FILTER_NAME_LIST,
      ],
      EMSP_BO_TARIFF_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<EmspBoTariffListOptionsModel>(
      EMSP_BO_TARIFF_LIST_DEFAULT_OPTIONS,
      EMSP_BO_TARIFF_LIST_OPTIONS_LSK,
    );

  const data = useMemo(
    () => ({
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      columnOptions,
      setColumnOptions,
    }),
    [
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      columnOptions,
      setColumnOptions,
    ],
  );

  return (
    <EmspBoTariffListContext.Provider value={data}>
      {children}
    </EmspBoTariffListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterEmspBoTariffListContext =
  createContext<DeepPartialSkipArrayKey<GetTariffsPaginatedModel> | null>(null);

export const useFilterEmspBoTariffListContext =
  (): DeepPartialSkipArrayKey<GetTariffsPaginatedModel> => {
    const context = useContext(FilterEmspBoTariffListContext);

    if (!context) {
      throw new Error(
        'useFilterEmspBoTariffListContext must be used within FilterEmspBoTariffListContext.Provider',
      );
    }

    return context;
  };

interface FilterEmspBoTariffListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetTariffsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterEmspBoTariffListContextProvider = ({
  data,
  children,
}: FilterEmspBoTariffListContextProviderProps): React.JSX.Element => {
  return (
    <FilterEmspBoTariffListContext.Provider value={data}>
      {children}
    </FilterEmspBoTariffListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchEmspBoTariffListContext =
  createContext<DeepPartialSkipArrayKey<GetTariffsPaginatedModel> | null>(null);

export const useFilterSearchEmspBoTariffListContext =
  (): DeepPartialSkipArrayKey<GetTariffsPaginatedModel> => {
    const context = useContext(FilterSearchEmspBoTariffListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchEmspBoTariffListContext must be used within FilterSearchEmspBoTariffListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchEmspBoTariffListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetTariffsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchEmspBoTariffListContextProvider = ({
  data,
  children,
}: FilterSearchEmspBoTariffListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchEmspBoTariffListContext.Provider value={data}>
      {children}
    </FilterSearchEmspBoTariffListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const PullEmspBoTariffContext =
  createContext<DeepPartialSkipArrayKey<PullTariffsModel> | null>(null);

export const usePullEmspBoTariffContext =
  (): DeepPartialSkipArrayKey<PullTariffsModel> => {
    const context = useContext(PullEmspBoTariffContext);

    if (!context) {
      throw new Error(
        'usePullEmspBoTariffContext must be used within PullEmspBoTariffContext.Provider',
      );
    }

    return context;
  };

interface PullEmspBoTariffContextProviderProps {
  data: DeepPartialSkipArrayKey<PullTariffsModel>;
  children: React.ReactNode;
}

export const PullEmspBoTariffContextProvider = ({
  data,
  children,
}: PullEmspBoTariffContextProviderProps): React.JSX.Element => (
  <PullEmspBoTariffContext.Provider value={data}>
    {children}
  </PullEmspBoTariffContext.Provider>
);
